import { Box, Flex, Text } from "@chakra-ui/react";
import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * The small building blocks of the Dashboard tab: a clickable number, a titled card, a donut and
 * a bar chart. Kept apart from the tab so it reads as layout and the pieces read as pieces.
 */

export function MetricTile(props: {
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: "default" | "danger";
  onClick?: () => void;
}) {
  const isDanger = props.tone === "danger";

  return (
    <Box
      _hover={props.onClick === undefined ? undefined : { borderColor: "blue.300" }}
      as={props.onClick === undefined ? "div" : "button"}
      bg={isDanger ? "red.50" : "gray.50"}
      borderColor={isDanger ? "red.200" : "transparent"}
      borderRadius="md"
      borderWidth={1}
      cursor={props.onClick === undefined ? "default" : "pointer"}
      px={4}
      py={3}
      textAlign="left"
      onClick={props.onClick}
    >
      <Text color={isDanger ? "red.700" : "gray.600"} fontSize="sm">
        {props.label}
      </Text>
      <Text color={isDanger ? "red.700" : "gray.900"} fontSize="3xl" fontWeight="semibold">
        {props.value}
      </Text>
      {props.hint === undefined ? null : (
        <Text color="gray.500" fontSize="xs">
          {props.hint}
        </Text>
      )}
    </Box>
  );
}

export function DashboardCard(props: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Box borderColor="gray.200" borderRadius="lg" borderWidth={1} p={4}>
      <Flex align="baseline" justify="space-between" mb={2}>
        <Text color="gray.700" fontSize="sm" fontWeight="semibold">
          {props.title}
        </Text>
        {props.hint === undefined ? null : (
          <Text color="blue.600" fontSize="xs">
            {props.hint}
          </Text>
        )}
      </Flex>
      {props.children}
    </Box>
  );
}

export type Slice = { key: string; label: string; value: number; color: string };

/** Colour key and counts under a chart, so the numbers are readable without hovering. */
function Legend(props: { slices: Slice[] }) {
  return (
    <Flex gap={4} mb={1} wrap="wrap">
      {props.slices.map((slice) => (
        <Flex key={slice.key} align="center" gap={1}>
          <Box bg={slice.color} borderRadius="sm" h="10px" w="10px" />
          <Text color="gray.600" fontSize="xs">
            {slice.label} {slice.value}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
}

export function Donut(props: {
  slices: Slice[];
  height?: number;
  /** Return nothing to leave a slice unclickable. */
  onSliceClick?: (key: string) => void;
}) {
  const total = props.slices.reduce((sum, slice) => sum + slice.value, 0);

  if (total === 0) {
    return (
      <Text color="gray.500" fontSize="sm" py={6} textAlign="center">
        Nothing to show.
      </Text>
    );
  }

  return (
    <Box>
      <Legend slices={props.slices} />
      <ResponsiveContainer height={props.height ?? 170} width="100%">
        <PieChart>
          <Pie
            cursor={props.onSliceClick === undefined ? "default" : "pointer"}
            data={props.slices}
            dataKey="value"
            innerRadius="58%"
            nameKey="label"
            outerRadius="85%"
            paddingAngle={1}
            stroke="none"
            onClick={(_slice, index) => {
              const clicked = props.slices.at(index);
              if (clicked !== undefined) {
                props.onSliceClick?.(clicked.key);
              }
            }}
          >
            {props.slices.map((slice) => (
              <Cell key={slice.key} fill={slice.color} />
            ))}
          </Pie>
          <ChartTooltip />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
}

export type BarDatum = { key: string; label: string; value: number; color: string };

/** One colour per bar, for a single series where the colour says something (past due or not). */
export function BarsByColor(props: {
  bars: BarDatum[];
  height?: number;
  onBarClick?: (key: string) => void;
}) {
  return (
    <ResponsiveContainer height={props.height ?? 170} width="100%">
      <BarChart data={props.bars} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
        <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
        <XAxis axisLine={false} dataKey="label" fontSize={11} tickLine={false} />
        <YAxis allowDecimals={false} axisLine={false} fontSize={11} tickLine={false} />
        <ChartTooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} />
        <Bar
          cursor={props.onBarClick === undefined ? "default" : "pointer"}
          dataKey="value"
          maxBarSize={36}
          name="Notes"
          radius={[4, 4, 0, 0]}
          onClick={(_bar, index) => {
            const clicked = props.bars.at(index);
            if (clicked !== undefined) {
              props.onBarClick?.(clicked.key);
            }
          }}
        >
          {props.bars.map((bar) => (
            <Cell key={bar.key} fill={bar.color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export type GroupedBar = { label: string; first: number | null; second: number | null };

/** Two measures side by side per category: the median and the slowest tenth, say. */
export function GroupedBars(props: {
  rows: GroupedBar[];
  firstName: string;
  secondName: string;
  firstColor: string;
  secondColor: string;
  height?: number;
}) {
  return (
    <Box>
      <Flex gap={4} mb={1}>
        {[
          { name: props.firstName, color: props.firstColor },
          { name: props.secondName, color: props.secondColor },
        ].map((item) => (
          <Flex key={item.name} align="center" gap={1}>
            <Box bg={item.color} borderRadius="sm" h="10px" w="10px" />
            <Text color="gray.600" fontSize="xs">
              {item.name}
            </Text>
          </Flex>
        ))}
      </Flex>
      <ResponsiveContainer height={props.height ?? 150} width="100%">
        <BarChart data={props.rows} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
          <XAxis axisLine={false} dataKey="label" fontSize={11} tickLine={false} />
          <YAxis axisLine={false} fontSize={11} tickLine={false} />
          <ChartTooltip cursor={{ fill: "rgba(0,0,0,0.04)" }} />
          <Bar
            dataKey="first"
            fill={props.firstColor}
            maxBarSize={22}
            name={props.firstName}
            radius={[4, 4, 0, 0]}
          />
          <Bar
            dataKey="second"
            fill={props.secondColor}
            maxBarSize={22}
            name={props.secondName}
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}
